/**
 * The Rithmo customer dashboard: a separate production build of this app (VITE_RITHMO_CUSTOMER_APP=true). Its pages
 * are the Control Board pages (customer/cb, owned in this repository; see its README.md)
 * on Rithmo's sign-in, signup and AI-assistant authorization. The normal dashboard build never sets it, so nothing here
 * changes it.
 *
 * Signup, email verification, password reset and org invitations all need email delivery. The customer build shows
 * them only when its deployment can send email (VITE_RITHMO_EMAIL_FLOWS=true); the normal build always shows them.
 */

type Env = Record<string, string | undefined>

export function customerFlags(env: Env | undefined): { customerApp: boolean; emailFlows: boolean } {
  const customerApp = env?.VITE_RITHMO_CUSTOMER_APP === 'true'
  return { customerApp, emailFlows: !customerApp || env?.VITE_RITHMO_EMAIL_FLOWS === 'true' }
}

const flags = customerFlags((import.meta as { env?: Env }).env)
export const CUSTOMER_APP = flags.customerApp
export const EMAIL_FLOWS = flags.emailFlows

/**
 * Customer navigation, in order: the Control Board's (Dashboard; then Setup: Agents, Connections,
 * Authority), plus Settings. adminOnly pages read /api/control-board, which answers 403 not_admin to anyone who is not
 * an organization admin; the customer routes enforce the same rule. Members get My agents (their own agents; their home),
 * Connections (their own integrations) and Settings.
 */
export interface CustomerNavItem { label: string; path: string; group: 'work' | 'setup'; adminOnly?: boolean; memberOnly?: boolean }

export const CUSTOMER_NAV: CustomerNavItem[] = [
  { label: 'My agents', path: '/my-agents', group: 'work', memberOnly: true },
  { label: 'Dashboard', path: '/dashboard', group: 'work', adminOnly: true },
  { label: 'Agents', path: '/agents', group: 'setup', adminOnly: true },
  { label: 'Connections', path: '/connections', group: 'setup' },
  { label: 'Authority', path: '/authority', group: 'setup', adminOnly: true },
  { label: 'Settings', path: '/settings', group: 'setup' },
]

export const CUSTOMER_ADMIN_HOME = '/dashboard'
export const CUSTOMER_MEMBER_HOME = '/my-agents'

export function customerHome(isAdmin: boolean): string {
  return isAdmin ? CUSTOMER_ADMIN_HOME : CUSTOMER_MEMBER_HOME
}

export function customerNavFor(isAdmin: boolean): CustomerNavItem[] {
  return CUSTOMER_NAV.filter((item) => (isAdmin ? !item.memberOnly : !item.adminOnly))
}

/** Settings sections the customer build shows: the account and its integrations, never meeting or billing settings. */
export const CUSTOMER_SETTINGS_SECTIONS: readonly string[] = ['profile', 'integrations']

/**
 * The section to open for a ?tab= / ?section= request. The customer build falls back to the account section for
 * anything it does not show; the normal build opens what was asked for, as before.
 */
export function settingsSectionFor(requested: string | null | undefined, customerApp: boolean = CUSTOMER_APP): string {
  const section = requested || 'profile'
  if (!customerApp) return section
  return CUSTOMER_SETTINGS_SECTIONS.includes(section) ? section : 'profile'
}

/** Where a signed-in user goes when no page asked for them: the role-aware customer home, or the normal dashboard. */
export const SIGNED_IN_HOME = CUSTOMER_APP ? '/' : '/dashboard'

/** The normal build's home, which /dashboard and /home redirect to: the Overview for org admins, Connections for members. */
export function normalHome(isAdmin: boolean): string {
  return isAdmin ? '/overview' : '/connections'
}
